import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, FlaskConical, X, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PLAN_LABEL = { free: "Free", pro: "Pro Host", pm: "Property Manager" };

/**
 * Owner-only floating control that lets the platform owner temporarily simulate
 * any subscription tier (or full owner access) WITHOUT affecting billing or
 * owner permissions. Rendered only when the authenticated user is the owner.
 */
export const OwnerTestMode = () => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // owner test-mode payload
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/owner/test-mode");
      if (r.data?.is_owner) setState(r.data);
      else setState(null);
    } catch (e) {
      /* silent — non-critical owner utility */
    }
  }, []);

  useEffect(() => {
    if (user?.is_owner) load();
    else setState(null);
  }, [user, load]);

  const onChange = async (value) => {
    setSaving(true);
    try {
      const r = await api.post("/owner/test-mode", { plan: value });
      setState(r.data);
      await refreshUser();
      const label = r.data?.test_mode_active
        ? `Simulating ${PLAN_LABEL[r.data.simulated_plan] || r.data.simulated_plan}`
        : "Owner access restored — full complimentary access";
      toast.success(label);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not update Owner Test Mode."));
    } finally {
      setSaving(false);
    }
  };

  if (!user?.is_owner || !state) return null;

  const currentValue = state.test_mode_active ? state.simulated_plan : "owner";
  const active = state.test_mode_active;

  // Collapsed pill
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="owner-test-mode-toggle"
        className={`fixed bottom-4 left-4 z-[200] flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold shadow-lg backdrop-blur transition-colors ${
          active
            ? "border-amber-300 bg-amber-50/90 text-amber-800 hover:bg-amber-100"
            : "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
        }`}
      >
        {active ? <FlaskConical className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
        <span>
          {active ? `Testing: ${PLAN_LABEL[state.simulated_plan] || state.simulated_plan}` : "Owner Mode"}
        </span>
        <ChevronUp className="h-3.5 w-3.5 opacity-70" />
      </button>
    );
  }

  // Expanded panel
  return (
    <div
      data-testid="owner-test-mode-panel"
      className="fixed bottom-4 left-4 z-[200] w-72 rounded-xl border border-border bg-background/95 p-4 shadow-xl backdrop-blur"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FlaskConical className="h-4 w-4" />
          </span>
          <span className="font-display text-sm font-semibold text-foreground">Owner Test Mode</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="owner-test-mode-close"
          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Collapse Owner Test Mode"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
        Preview the app as any tier. This never affects billing or your owner permissions.
      </p>

      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Simulate tier</span>
        {active ? (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800" data-testid="owner-test-mode-status">
            Test mode on
          </Badge>
        ) : (
          <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary" data-testid="owner-test-mode-status">
            Full access
          </Badge>
        )}
      </div>

      <Select value={currentValue} onValueChange={onChange} disabled={saving}>
        <SelectTrigger data-testid="owner-test-mode-select" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[210]">
          {(state.options || []).map((opt) => (
            <SelectItem key={opt.id} value={opt.id} data-testid={`owner-test-mode-option-${opt.id}`}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="owner-effective-plan">
        {saving ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…</>
        ) : (
          <>
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            Currently experiencing: <span className="font-semibold text-foreground">{PLAN_LABEL[state.effective_plan] || state.effective_plan}</span>
          </>
        )}
      </div>

      {active && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full"
          onClick={() => onChange("owner")}
          disabled={saving}
          data-testid="owner-test-mode-reset"
        >
          Exit test mode
        </Button>
      )}
    </div>
  );
};

export default OwnerTestMode;
